// R123 (Jesse: "the options buttons … are cluttered and all over the place"):
// the Issues board's decision sheets, replacing the native prompt() /
// confirm() / alert() for Dismiss (row + bulk), Strike (row + bulk), Claim,
// a plain notice, and the bulk confirms.
//
// CONTRACT (R123 §8.7): askDismissReason / askStrike / askClaim / notice /
// askBulkConfirm. Built on the woSync shell (sheet / head / sec / pill), so
// every sheet:
//   - carries .iss-modal-wrap (the board's 15-minute refresh is vetoed while
//     one is open) and sits at z-index 9600, above the detail / route modals;
//   - is a centred card on wide screens and a bottom sheet on phones (the
//     shell's ≤ 720px rule covers the ≤ 640px contract), controls ≥ 36px,
//     inputs 16px at 390 (no iOS zoom);
//   - closes on Esc, on the backdrop and on ✕ / Cancel (all = cancel), keeps
//     Tab inside the sheet, and puts focus on its one primary button;
//   - escapes every value it shows. Tokens only, no new colours.
import { sheet, head, sec, pill } from './woSync';

/** What a person may pick when dismissing (lib/findingState.js HUMAN_REASON_CODES; 'stock_check' is the automatic rule's). */
export const HUMAN_REASON_CODES = Object.freeze(['not_issue', 'duplicate', 'other', 'present'] as const);
export type HumanReasonCode = typeof HUMAN_REASON_CODES[number];
export const REASON_CODE_LABEL: Readonly<Record<HumanReasonCode, string>> = Object.freeze({
  not_issue: 'Not an issue',
  duplicate: 'Duplicate',
  other: 'Other',
  present: 'Visually checked, present',
});
export interface DismissReason { reason: string; reason_code: HumanReasonCode }

const esc = (s: any) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** The sheet's sub line: the one title in quotes, or "N issues". Escaped. */
const titleLine = (titles: string[] | undefined, n: number) => (n === 1 && titles && titles[0] ? `“${esc(clip(String(titles[0]), 70))}”` : esc(plural(n, 'issue')));
/** A short list of the batch's titles (≤ 6 + "N more"). */
const titleList = (titles: string[] | undefined, n: number) => {
  const t = (titles || []).filter(Boolean).map(String);
  if (n <= 1 || !t.length) return '';
  return `${sec('Issues', n)}<div class="iws-card iws-list">${t.slice(0, 6).map(x => `<div class="iws-li"><span class="iws-dot"></span><span class="iws-lt">${esc(clip(x, 70))}</span></div>`).join('')}${n > 6 ? `<div class="iws-li iws-more">+${n - 6} more</div>` : ''}</div>`;
};

// ── the runner: one sheet, one answer ───────────────────────────────────────
// wire() binds the sheet's own controls and calls done(value) to answer; every
// cancel path (✕, Cancel, Esc, backdrop) answers `cancelled`.
function run<T>(html: string, cancelled: T, wire: (wrap: HTMLElement, done: (v: T) => void) => void, focusSel = '[data-iws-go]'): Promise<T> {
  ensureCss();
  return new Promise<T>(resolve => {
    const { wrap, close } = sheet(html);
    wrap.classList.add('iss-sh');
    let answered = false;
    const prevFocus = document.activeElement as HTMLElement | null;
    const done = (v: T) => {
      if (answered) return;
      answered = true;
      wrap.removeEventListener('keydown', trap);
      close();
      try { if (prevFocus && prevFocus.isConnected) prevFocus.focus(); } catch { /* gone */ }
      resolve(v);
    };
    // Tab stays inside the sheet
    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const f = Array.from(wrap.querySelectorAll<HTMLElement>('button, [href], input, textarea, select')).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    wrap.addEventListener('keydown', trap);
    wrap.querySelectorAll('[data-iws-cancel]').forEach(el => el.addEventListener('click', () => done(cancelled)));
    wrap.addEventListener('click', (e) => { if (e.target === wrap) done(cancelled); });
    wire(wrap, done);
    (wrap.querySelector(focusSel) as HTMLElement | null)?.focus();
  });
}
const foot = (primary: string, opts: { danger?: boolean; cancel?: string | false } = {}) => `
  <div class="iws-foot">
    <button type="button" class="btn ${opts.danger ? 'btn-danger' : 'btn-primary'} iws-primary" data-iws-go>${primary}</button>
    ${opts.cancel === false ? '' : `<button type="button" class="btn btn-ghost iws-secondary" data-iws-cancel>${esc(opts.cancel || 'Cancel')}</button>`}
  </div>`;

// ── Dismiss (one row or a batch) ────────────────────────────────────────────
/**
 * Dismiss one row or a batch: a reason code (default not_issue) + optional words. null = cancelled.
 * `codes` (optional) narrows the choices, e.g. no "Visually checked, present" for a review theme.
 */
export function askDismissReason(o: { n: number; titles?: string[]; codes?: readonly HumanReasonCode[] }): Promise<DismissReason | null> {
  const n = Math.max(1, Number(o.n) || 1);
  const codes = (o.codes && o.codes.length ? HUMAN_REASON_CODES.filter(c => o.codes!.includes(c)) : [...HUMAN_REASON_CODES]) as HumanReasonCode[];
  const order: HumanReasonCode[] = ['not_issue', 'duplicate', 'present', 'other'];
  const shown = order.filter(c => codes.includes(c));
  const first = shown[0] || 'not_issue';
  const html = `
    ${head('✕', n === 1 ? 'Dismiss this issue?' : `Dismiss ${n} issues?`, titleLine(o.titles, n))}
    <div class="iws-body">
      ${titleList(o.titles, n)}
      ${sec('Why')}
      <div class="iss-sh-codes" role="radiogroup" aria-label="Why dismiss">
        ${shown.map(c => `<label class="iss-sh-code${c === first ? ' on' : ''}"><input type="radio" name="iss-sh-rc" value="${c}" data-iss-code${c === first ? ' checked' : ''}><span>${esc(REASON_CODE_LABEL[c])}</span></label>`).join('')}
      </div>
      ${sec('Note')}
      <textarea class="input iws-ta" data-iss-reason rows="2" maxlength="300" placeholder="Optional. For example: the owner already fixed it"></textarea>
      <div class="iws-help" data-iss-help>The reason shows on the dismissed row. Restore brings ${n === 1 ? 'it' : 'them'} back.</div>
    </div>
    ${foot(n === 1 ? 'Dismiss' : `Dismiss ${n}`)}`;
  return run<DismissReason | null>(html, null, (wrap, done) => {
    const ta = wrap.querySelector('[data-iss-reason]') as HTMLTextAreaElement;
    const help = wrap.querySelector('[data-iss-help]') as HTMLElement;
    const code = (): HumanReasonCode => {
      const el = wrap.querySelector('[data-iss-code]:checked') as HTMLInputElement | null;
      const v = el ? el.value : first;
      return (HUMAN_REASON_CODES as readonly string[]).includes(v) ? v as HumanReasonCode : 'not_issue';
    };
    wrap.querySelectorAll<HTMLInputElement>('[data-iss-code]').forEach(r => r.addEventListener('change', () => {
      wrap.querySelectorAll<HTMLInputElement>('[data-iss-code]').forEach(x => x.closest('.iss-sh-code')?.classList.toggle('on', x.checked));
      help.classList.remove('iss-sh-bad');
      if (code() === 'other') { ta.placeholder = 'Say why in a few words'; ta.focus(); } else ta.placeholder = 'Optional. For example: the owner already fixed it';
    }));
    ta.addEventListener('input', () => help.classList.remove('iss-sh-bad'));
    wrap.querySelector('[data-iws-go]')?.addEventListener('click', () => {
      const reason = String(ta.value || '').trim().slice(0, 300);
      const c = code();
      if (c === 'other' && !reason) {   // "Other" with no words says nothing to the next person
        help.textContent = 'Say why in a few words when you pick Other.';
        help.classList.add('iss-sh-bad');
        ta.focus();
        return;
      }
      done({ reason, reason_code: c });
    });
  });
}

// ── Strike (danger) ─────────────────────────────────────────────────────────
/** Flag a cleaner strike for one row or a batch. */
export function askStrike(o: { n: number; titles?: string[] }): Promise<boolean> {
  const n = Math.max(1, Number(o.n) || 1);
  const html = `
    ${head('⚠', n === 1 ? 'Flag a cleaner strike?' : `Flag ${n} strike candidates?`, n === 1 && o.titles && o.titles[0] ? titleLine(o.titles, 1) : 'An admin reviews each one before any strike is recorded')}
    <div class="iws-body">
      ${titleList(o.titles, n)}
      <div class="iws-card">
        <div class="iws-pills">${pill('Strike review', 'bad')}${pill('Admin decides', '')}</div>
        <div class="iws-note">${n === 1 ? 'This goes' : 'These go'} to the strike review queue against the cleaner of that clean. Nothing counts until an admin confirms it.</div>
      </div>
      <div class="iws-help">The issue itself stays where it is on the board.</div>
    </div>
    ${foot(n === 1 ? 'Flag strike' : `Flag ${n} strikes`, { danger: true })}`;
  return run<boolean>(html, false, (wrap, done) => {
    wrap.classList.add('iss-sh-danger');
    wrap.querySelector('[data-iws-go]')?.addEventListener('click', () => done(true));
  });
}

// ── Claim ───────────────────────────────────────────────────────────────────
/** Open a damage claim from a row. */
export function askClaim(row: { title?: string; listing_name?: string } | null | undefined): Promise<boolean> {
  const t = clip(String((row && row.title) || 'this issue'), 70);
  const where = row && row.listing_name ? ` · ${esc(row.listing_name)}` : '';
  const html = `
    ${head('⚖', 'Open a damage claim?', `“${esc(t)}”${where}`)}
    <div class="iws-body">
      <div class="iws-note">This starts a claim under Damages &amp; Claims in Remote Operations, with this issue's photos and notes. The issue stays on the board.</div>
      <div class="iws-help">One claim per issue: opening it again takes you to the same claim.</div>
    </div>
    ${foot('Open claim')}`;
  return run<boolean>(html, false, (wrap, done) => {
    wrap.querySelector('[data-iws-go]')?.addEventListener('click', () => done(true));
  });
}

// ── a plain notice ──────────────────────────────────────────────────────────
/** A plain message with one OK. */
export function notice(title: string, body: string): Promise<void> {
  const lines = String(body || '').split(/\n+/).map(s => s.trim()).filter(Boolean);
  const html = `
    ${head('ℹ', String(title || 'Notice'), '')}
    <div class="iws-body">${lines.length ? lines.map(l => `<div class="iws-note">${esc(l)}</div>`).join('') : ''}<div class="iss-sh-gap"></div></div>
    ${foot('OK', { cancel: false })}`;
  return run<void>(html, undefined, (wrap, done) => {
    wrap.querySelector('[data-iws-go]')?.addEventListener('click', () => done(undefined));
  });
}

// ── bulk confirms ───────────────────────────────────────────────────────────
const VERB_ICON: Record<string, string> = { resolve: '✓', dismiss: '✕', restore: '↩', 'mark triaged': '✓', merge: '⧉', strike: '⚠', assign: '→' };
const VERB_HELP: Record<string, string> = {
  resolve: 'They move to Done. Restore brings them back.',
  dismiss: 'They leave the open lists. Restore brings them back.',
  restore: 'They go back to the open lists, where they were before.',
  'mark triaged': 'They leave To triage and wait in To schedule until someone routes them.',
  merge: 'They become one issue. Split it back out from the issue itself.',
};
/** "Resolve 12 issues?" and the like. verb is the action word ("Resolve", "Dismiss", "Restore", "Mark triaged"). */
export function askBulkConfirm(o: { verb: string; n: number; titles?: string[] }): Promise<boolean> {
  const verb = String(o.verb || 'Apply').trim() || 'Apply';
  const n = Math.max(1, Number(o.n) || 1);
  const key = verb.toLowerCase();
  const html = `
    ${head(VERB_ICON[key] || '•', `${verb} ${plural(n, 'issue')}?`, n === 1 && o.titles && o.titles[0] ? titleLine(o.titles, 1) : '')}
    <div class="iws-body">
      ${titleList(o.titles, n)}
      ${VERB_HELP[key] ? `<div class="iws-note">${esc(VERB_HELP[key])}</div>` : ''}
      <div class="iss-sh-gap"></div>
    </div>
    ${foot(esc(`${verb} ${n}`))}`;
  return run<boolean>(html, false, (wrap, done) => {
    wrap.querySelector('[data-iws-go]')?.addEventListener('click', () => done(true));
  });
}

// ── styles (prefixed, injected once; tokens only) ───────────────────────────
let cssDone = false;
function ensureCss(): void {
  if (cssDone || typeof document === 'undefined') return;
  if (document.getElementById('iss-sh-css')) { cssDone = true; return; }
  const st = document.createElement('style');
  st.id = 'iss-sh-css';
  st.textContent = `
  .iss-sh-codes { display:flex; flex-wrap:wrap; gap:8px; }
  .iss-sh-code { position:relative; display:inline-flex; align-items:center; min-height:36px; padding:0 14px; border-radius:var(--radius-pill); border:1.5px solid var(--border); background:var(--card); color:var(--text); font-size:12.5px; font-weight:600; cursor:pointer; user-select:none; box-sizing:border-box; }
  .iss-sh-code input { position:absolute; opacity:0; width:1px; height:1px; margin:0; pointer-events:none; }
  .iss-sh-code:hover { border-color:var(--accent-border); }
  .iss-sh-code.on { border-color:var(--accent); background:var(--accent-soft); color:var(--accent-hover); }
  .iss-sh-code:has(input:focus-visible) { box-shadow:var(--focus-ring); }
  .iss-sh-danger .iws-ic { background:var(--danger-soft); color:var(--danger-text); }
  .iws-help.iss-sh-bad { color:var(--danger-text); font-weight:600; }
  .iss-sh-gap { height:6px; }
  @media (max-width: 720px) {
    .iss-sh-code { min-height:40px; font-size:13px; }
  }
  `;
  document.head.appendChild(st);
  cssDone = true;
}
